import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { SiteIcon } from "@/components/icons/siteIcons";
import {
  iconography,
  trackerMetricCategoryLabels,
  trackerMetricIconography,
  trackerMetricOrder,
  type TrackerMetricId,
} from "@/design-system/tokens/iconography";
import { cn } from "@/lib/utils";
import type { HealthMetric, MetricCategory } from "@/types/tracker";

interface TrackerOption {
  id: TrackerMetricId;
  title: string;
  iconKey: (typeof trackerMetricIconography)[TrackerMetricId]["iconKey"];
  iconColorClassName: string;
  category: MetricCategory;
  unit: string;
}

const trackerOptions: TrackerOption[] = trackerMetricOrder.map((metricId) => {
  const metric = trackerMetricIconography[metricId];

  return {
    id: metricId,
    title: metric.label,
    iconKey: metric.iconKey,
    iconColorClassName: metric.iconColorClassName,
    category: metric.category,
    unit: metric.unit,
  };
});

interface TrackerSelectionDropdownProps {
  onTrackerSelect: (tracker: HealthMetric) => void;
  disabled?: boolean;
  triggerClassName?: string;
  triggerLabel?: string;
}

export const TrackerSelectionDropdown = ({
  onTrackerSelect,
  disabled,
  triggerClassName,
  triggerLabel = "Tracker",
}: TrackerSelectionDropdownProps) => {
  const [isOpen, setIsOpen] = useState(false);

  const handleTrackerSelect = (tracker: TrackerOption) => {
    const healthMetric: HealthMetric = {
      id: tracker.id,
      title: tracker.title,
      value: "",
      unit: tracker.unit,
      period: "current",
      category: tracker.category,
    };

    onTrackerSelect(healthMetric);
    setIsOpen(false);
  };

  // Group trackers by category
  const groupedTrackers = trackerOptions.reduce(
    (acc, tracker) => {
      if (!acc[tracker.category]) {
        acc[tracker.category] = [];
      }
      acc[tracker.category].push(tracker);
      return acc;
    },
    {} as Record<string, TrackerOption[]>
  );

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          disabled={disabled}
          className={cn(
            "z-label h-9 rounded-md bg-transparent px-4 text-foreground shadow-none transition-colors hover:bg-muted/70 hover:text-foreground active:bg-muted/80",
            isOpen && "bg-muted/80 text-foreground",
            triggerClassName
          )}
        >
          <SiteIcon name="plus" className={iconography.size.inline} />
          <span>{triggerLabel}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        {Object.entries(groupedTrackers).map(
          ([category, trackers], categoryIndex) => (
            <div key={category}>
              {categoryIndex > 0 && <DropdownMenuSeparator />}
              <div className="z-eyebrow px-2 py-1.5 text-muted-foreground">
                {trackerMetricCategoryLabels[category as MetricCategory] ||
                  category}
              </div>
              {trackers.map((tracker) => (
                <DropdownMenuItem
                  key={tracker.id}
                  onClick={() => handleTrackerSelect(tracker)}
                  className="z-body flex cursor-pointer items-center gap-2"
                >
                  <SiteIcon
                    name={tracker.iconKey}
                    className={`${iconography.size.inline} ${tracker.iconColorClassName}`}
                  />
                  <span>{tracker.title}</span>
                </DropdownMenuItem>
              ))}
            </div>
          )
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
